import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import AdsSlider from "../components/AdsSlider";
import ItemsList from "../components/ItemsList";
import Cart from "../components/cart";
import Login from "./Login";
import axios from "axios";
import { API_URL } from "../api";

// Category icons — drop these PNG files into e.g. src/assets/icons/
// (adjust the import paths below to wherever you keep them in your project)
import vegetablesIcon from "../assets/icons/vegetables.png";
import flowersIcon from "../assets/icons/flowers.png";
import accessoriesIcon from "../assets/icons/accessories.png";
import garlandIcon from "../assets/icons/garland.png";
import courierIcon from "../assets/icons/courier.png";

// ==========================================
// FOOTER SOCIAL ICONS
// ==========================================
const FooterIcons = {
  Instagram: ({ size = 20, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="2.5" y="2.5" width="19" height="19" rx="5.5" stroke={color} strokeWidth="1.8" />
      <circle cx="12" cy="12" r="4.3" stroke={color} strokeWidth="1.8" />
      <circle cx="17.4" cy="6.6" r="1.15" fill={color} />
    </svg>
  ),
  WhatsApp: ({ size = 20, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 2.5A9.5 9.5 0 0 0 3.9 17.1L2.5 21.5l4.53-1.37A9.5 9.5 0 1 0 12 2.5z"
        stroke={color}
        strokeWidth="1.8"
      />
      <path
        d="M8.3 8.6c.15-.55.6-.55.9-.55h.35c.2 0 .4.05.55.4.2.45.65 1.6.7 1.7.05.1.1.25 0 .4-.1.15-.15.25-.3.4-.15.15-.3.3-.15.6.15.3.7 1.15 1.5 1.85.9.85 1.6 1.1 1.9 1.25.3.15.5.1.65-.05.2-.2.75-.85.95-1.15.2-.3.4-.25.65-.15.25.1 1.65.8 1.9.95.25.15.45.2.5.35.05.15.05.9-.25 1.75-.3.85-1.7 1.55-2.35 1.6-.6.1-1.35.15-2.15-.15-.5-.15-1.15-.35-2-.7-3.5-1.5-5.75-5-5.9-5.25-.15-.25-1.3-1.75-1.3-3.3 0-1.55.8-2.3 1.1-2.6z"
        fill={color}
      />
    </svg>
  ),
  Email: ({ size = 20, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="2.5" y="4.5" width="19" height="15" rx="3" stroke={color} strokeWidth="1.8" />
      <path d="M3.5 6.5l8.5 6.5 8.5-6.5" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

// ==========================================
// CATEGORY LIST — Zepto-style icon row, using your own PNG icons
// ==========================================
const CATEGORIES = [
  { key: "vegetables", label: "Vegetables", sub: "Market-fresh", icon: vegetablesIcon, bg: "#eaf7ea", action: "scroll", target: "vegetables-section" },
  { key: "flowers", label: "Flowers", sub: "Fresh & pretty", icon: flowersIcon, bg: "#fdeef5", action: "scroll", target: "flowers-section" },
  { key: "accessories", label: "Accessories", sub: "Mobile & more", icon: accessoriesIcon, bg: "#eef1fb", action: "scroll", target: "accessories-section" },
  { key: "garland", label: "Garlands", sub: "Made to order", icon: garlandIcon, bg: "#fdf3e7", action: "navigate", target: "/custom-garland" },
  { key: "courier", label: "Courier", sub: "1-day delivery", icon: courierIcon, bg: "#eef7fb", action: "navigate", target: "/courier" },
];

export default function Home() {
  const { t } = useTranslation();
  const [cartCount, setCartCount] = useState(0);
  const [cartItems, setCartItems] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [cartRefresh, setCartRefresh] = useState(0);
  const [showLogin, setShowLogin] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);

  const navigate = useNavigate();
  const headerRef = useRef(null);

  // Scrolls to an in-page section (used by footer Category links, since
  // Vegetables/Flowers render inline on this page — not on their own route).
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const user_id = localStorage.getItem("phone") || "guest";
  const isLoggedIn = () => localStorage.getItem("isLoggedIn") === "true";

  const [pendingCartIds, setPendingCartIds] = useState({});
  // Guards against out-of-order network responses: if requests A then B fire
  // in that order but B's response arrives first, A's response landing later
  // must not be allowed to overwrite the newer state - this is what was
  // causing quantities to visibly "jump" during fast clicking.
  const cartRequestId = useRef(0);

  const loadCart = async () => {
    if (!isLoggedIn()) return;
    const requestId = ++cartRequestId.current;
    try {
      const res = await axios.get(`${API_URL}/cart/${user_id}`);
      if (requestId !== cartRequestId.current) return; // a newer request already landed - ignore this stale one
      if (res.data.success) {
        setCartItems(res.data.cart);
        const uniqueItems = [...new Set(res.data.cart.map(item => item.product_id))];
        setCartCount(uniqueItems.length);
      }
    } catch (err) { console.log("Load cart error:", err); }
  };

  const addToCart = async (product_id, category) => {
    if (!isLoggedIn()) { setShowLogin(true); return; }
    const pendingKey = `${product_id}-${category}`;
    if (pendingCartIds[pendingKey]) return; // ignore double-clicks while one is in flight
    setPendingCartIds((p) => ({ ...p, [pendingKey]: true }));
    // Optimistic bump so the badge/UI responds instantly instead of waiting
    // on a full round trip + reload.
    setCartCount((c) => c + 1);
    try {
      await axios.post(`${API_URL}/cart/add`, { user_id, product_id, category });
      await loadCart(); // reconcile with the real server state (also corrects the optimistic count if needed)
      setCartRefresh(prev => prev + 1);
    } catch (err) {
      console.error(err);
      await loadCart(); // roll back the optimistic bump on failure
    } finally {
      setPendingCartIds((p) => {
        const next = { ...p };
        delete next[pendingKey];
        return next;
      });
    }
  };

  const updateQty = async (id, action) => {
    if (pendingCartIds[id]) return; // ignore rapid repeat clicks on the same item
    const item = cartItems.find(i => i.id === id);
    if (!item) return;
    const willRemove = action === "decrease" && item.quantity <= 1;

    setPendingCartIds((p) => ({ ...p, [id]: true }));
    // Optimistic local update - update the visible quantity immediately
    // instead of waiting for a server round trip.
    setCartItems((prev) =>
      willRemove
        ? prev.filter((i) => i.id !== id)
        : prev.map((i) => (i.id === id ? { ...i, quantity: action === "increase" ? i.quantity + 1 : i.quantity - 1 } : i))
    );

    try {
      if (willRemove) await axios.delete(`${API_URL}/cart/${id}`);
      else await axios.put(`${API_URL}/cart/${action}/${id}`);
      // No forced full reload here - the optimistic update above is already
      // correct for the common case, and skipping the extra round trip is
      // exactly what stops the quantity from visibly jumping around.
      setCartRefresh(prev => prev + 1);
    } catch (err) {
      console.error(err);
      await loadCart(); // something went wrong - resync with the real server state
    } finally {
      setPendingCartIds((p) => {
        const next = { ...p };
        delete next[id];
        return next;
      });
    }
  };

  useEffect(() => { loadCart(); }, []);

  // Prevent background scrolling when cart is open
  useEffect(() => {
    document.body.style.overflow = showCart ? "hidden" : "auto";
  }, [showCart]);

  return (
    <div style={styles.page}>
      <style>{`
        .shiper-category-row::-webkit-scrollbar { display: none; }
        .shiper-category-row { scrollbar-width: none; -ms-overflow-style: none; }
        .shiper-category-item:hover { color: #172033; }
      `}</style>
      <Header ref={headerRef} cartCount={cartCount} openCart={() => setShowCart(true)} />

      <div style={{ marginTop: "88px", background: "#8ec5fc" }}>
        {/* --- COMPACT CATEGORY NAV BAR --- */}
        <section style={styles.categorySection} aria-label="ShiperBox categories">
          <div style={styles.categoryRow} className="shiper-category-row">
            {CATEGORIES.map((cat) => {
              const isActive = activeCategory === cat.key;
              return (
                <button
                  key={cat.key}
                  style={{ ...styles.categoryItem, ...(isActive ? styles.categoryItemActive : null) }}
                  className="shiper-category-item"
                  onClick={() => {
                    setActiveCategory(cat.key);
                    cat.action === "scroll" ? scrollToSection(cat.target) : navigate(cat.target);
                  }}
                >
                  <img
                    src={cat.icon}
                    alt=""
                    style={{ ...styles.categoryIconImg, ...(isActive ? styles.categoryIconImgActive : null) }}
                  />
                  <span>{cat.label}</span>
                </button>
              );
            })}
          </div>
        </section>

        <AdsSlider />
      </div>

      <div style={styles.content}>
        
        <div id="vegetables-section" style={{ scrollMarginTop: "130px" }}>
          <ItemsList 
            title={t("home.freshVegetables")} 
            category="vegetables" 
            addToCart={addToCart} 
            updateQty={updateQty} 
            cartItems={cartItems} 
            pendingIds={pendingCartIds}
            rows={2} 
          />
        </div>
        
        <div id="flowers-section" style={{ scrollMarginTop: "130px" }}>
          <ItemsList 
            title={t("categories.flowers")} 
            category="flowers" 
            addToCart={addToCart} 
            updateQty={updateQty} 
            cartItems={cartItems} 
            pendingIds={pendingCartIds}
            rows={2} 
          />
        </div>

        <div id="accessories-section" style={{ scrollMarginTop: "130px" }}>
          <ItemsList 
            title={t("categories.accessories", "Mobile Accessories")} 
            category="accessories" 
            addToCart={addToCart} 
            updateQty={updateQty} 
            cartItems={cartItems} 
            pendingIds={pendingCartIds}
            rows={2} 
          />
        </div>

        {/* --- CUSTOM GARLAND + 1-DAY COURIER, SIDE BY SIDE WITH IMAGES --- */}
        <div style={styles.sectionWrapper}>
          <div style={styles.dualBannerRow}>

            {/* --- CUSTOM GARLAND CARD --- */}
            <div style={styles.imageBannerWrapper}>
              <h2 style={styles.imageBannerHeading}>{t("home.bespokeGarland", "Emergency Custom Flower Garland")}</h2>
              <div style={styles.imageBannerCard}>
                <img
                  src="https://res.cloudinary.com/cj5eyjpf/image/upload/v1786521043/flowergarland_ni3pxp.jpg"
                  alt={t("home.customGarlands")}
                  style={styles.imageBannerImg}
                />
                <div style={styles.imageBannerOverlay} />
                <div style={styles.imageBannerContent}>
                  <div style={styles.imageBannerTextTop}>
                    <p style={styles.imageBannerSubtitle}>{t("home.garlandDesc", "Need it in a hurry? We'll craft and deliver a custom garland the same day.")}</p>
                  </div>
                  <div style={styles.imageBannerBtnRow}>
                    <button style={styles.bannerBtn} onClick={() => navigate("/custom-garland")}>
                      {t("mobile.orderNow", "ORDER NOW")}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* --- 1-DAY COURIER CARD --- */}
            <div style={styles.imageBannerWrapper}>
              <h2 style={styles.imageBannerHeading}>{t("home.expressCourier", "1 Day Courier Parcel")}</h2>
              <div style={styles.imageBannerCard}>
                <img
                  src="https://res.cloudinary.com/cj5eyjpf/image/upload/v1786521034/1daycor_hn0vsx.jpg"
                  alt={t("home.oneDayDelivery")}
                  style={styles.imageBannerImg}
                />
                <div style={styles.imageBannerOverlay} />
                <div style={styles.imageBannerContent}>
                  <div style={styles.imageBannerTextTop}>
                    <p style={styles.imageBannerSubtitle}>{t("home.courierDesc", "Fast, reliable parcel delivery — dropped off within 24 hours.")}</p>
                  </div>
                  <div style={styles.imageBannerBtnRow}>
                    <button style={styles.bannerBtn} onClick={() => navigate("/courier")}>
                      {t("home.requestCourier", "ORDER NOW")}
                    </button>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

      </div>

      {/* ========================================== */}
      {/* FOOTER — Blinkit-style desktop footer */}
      {/* ========================================== */}
      <footer style={styles.footer}>
        <div style={styles.footerTop}>

          <div style={styles.footerBrandCol}>
            <div style={styles.footerLogoRow}>
              <span style={styles.footerLogoShiper}>Shiper</span>
              <span style={styles.footerLogoBox}>Box</span>
            </div>
            <p style={styles.footerTagline}>
              {t("home.footerTagline")}
            </p>
            <div style={styles.footerSocialRow}>
              <a
                href="https://instagram.com/shiperbox"
                target="_blank"
                rel="noopener noreferrer"
                style={styles.footerSocialBtn}
                aria-label="Instagram"
              >
                <FooterIcons.Instagram size={18} color="#555" />
              </a>
              <a
                href="https://wa.me/916301912803?text=Hi%2C%20I%20have%20a%20question%20about%20ShiperBox"
                target="_blank"
                rel="noopener noreferrer"
                style={styles.footerSocialBtn}
                aria-label="WhatsApp"
              >
                <FooterIcons.WhatsApp size={18} color="#555" />
              </a>
              <a
                href="mailto:support@shiperbox.com"
                style={styles.footerSocialBtn}
                aria-label="Email"
              >
                <FooterIcons.Email size={18} color="#555" />
              </a>
            </div>
          </div>

          <div style={styles.footerLinkCol}>
            <h4 style={styles.footerColTitle}>{t("home.categoriesFooter")}</h4>
            <a style={styles.footerLink} onClick={() => scrollToSection("vegetables-section")}>{t("categories.vegetables")}</a>
            <a style={styles.footerLink} onClick={() => scrollToSection("flowers-section")}>{t("categories.flowers")}</a>
            <a style={styles.footerLink} onClick={() => scrollToSection("accessories-section")}>{t("categories.accessories", "Mobile Accessories")}</a>
            <a style={styles.footerLink} onClick={() => navigate("/custom-garland")}>{t("home.garlands")}</a>
            <a style={styles.footerLink} onClick={() => navigate("/courier")}>{t("home.courierService")}</a>
          </div>

          <div style={styles.footerLinkCol}>
            <h4 style={styles.footerColTitle}>{t("home.companyFooter")}</h4>
            <a style={styles.footerLink} onClick={() => headerRef.current?.openAccountSection("about")}>{t("home.aboutUs")}</a>
            <a style={styles.footerLink} onClick={() => headerRef.current?.openAccountSection("orders")}>{t("home.myOrders")}</a>
            <a style={styles.footerLink} onClick={() => headerRef.current?.openAccountSection("address")}>{t("home.myAddress")}</a>
            <a
              style={styles.footerLink}
              href="https://wa.me/916301912803?text=Hi%2C%20I%20need%20help%20with%20my%20ShiperBox%20order"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("home.needHelp")}
            </a>
          </div>

          <div style={styles.footerLinkCol}>
            <h4 style={styles.footerColTitle}>{t("home.getInTouch")}</h4>
            <a style={styles.footerLink} href="mailto:shiperbox@gmail.com">shiperbox@gmail.com</a>
            <a
              style={styles.footerLink}
              href="https://wa.me/916301912803"
              target="_blank"
              rel="noopener noreferrer"
            >
              +91 63019 12803
            </a>
            <a
              style={styles.footerLink}
              href="https://instagram.com/shiperbox"
              target="_blank"
              rel="noopener noreferrer"
            >
              @shiperbox on Instagram
            </a>
          </div>

        </div>

        <div style={styles.footerDivider} />

        <div style={styles.footerBottom}>
          <p style={styles.footerCopyright}>
            © {new Date().getFullYear()} ShiperBox. All rights reserved.
          </p>
          <p style={styles.footerMadeWith}>{t("home.madeWith")}</p>
        </div>
      </footer>
      {showCart && (
        <div style={styles.cartOverlay} onClick={() => setShowCart(false)}></div>
      )}

      {/* Full-Height Cart Wrapper */}
      <div style={styles.cartWrapper(showCart)}>
        {showCart && isLoggedIn() && (
          <Cart user_id={user_id} refresh={cartRefresh} onCartChange={loadCart} closeCart={() => setShowCart(false)} />
        )}
      </div>

      {/* Login Modal */}
      {showLogin && (
        <div style={styles.overlay}>
          <div style={styles.loginBox}>
            <button style={styles.closeBtn} onClick={() => setShowLogin(false)}>×</button>
            <Login onLoginSuccess={() => { setShowLogin(false); loadCart(); }} />
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(180deg, #f7fbff 0%, #f5f6fb 32%, #ffffff 100%)",
  },
  // Compact inline category nav bar (icon + label, small underline on active)
  categorySection: {
    background: "#8ec5fc",
    borderTop: "1px solid rgba(23,32,51,0.1)",
    borderBottom: "1px solid rgba(23,32,51,0.1)",
  },
  categoryRow: {
    maxWidth: "1500px",
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
    gap: "30px",
    padding: "0 20px",
    overflowX: "auto",
  },
  categoryItem: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    border: "none",
    borderBottom: "2px solid transparent",
    background: "transparent",
    cursor: "pointer",
    flexShrink: 0,
    padding: "13px 2px 11px",
    fontSize: "14px",
    fontWeight: "600",
    color: "#3c4655",
    whiteSpace: "nowrap",
  },
  categoryItemActive: {
    color: "#7b2ff7",
    borderBottom: "2px solid #7b2ff7",
  },
  categoryIconImg: {
    width: "20px",
    height: "20px",
    objectFit: "contain",
    opacity: 0.85,
  },
  categoryIconImgActive: {
    opacity: 1,
  },
  content: { padding: "26px 20px 20px", maxWidth: "1600px", margin: "0 auto" },
  sectionWrapper: { marginTop: "46px", marginBottom: "46px", padding: "0 130px" },
  sectionTitle: {
    fontSize: "25px", fontWeight: "850", margin: "0 20px 20px 0", color: "#172033",
    letterSpacing: "-.4px",
  },
  
  // Shared Banner Styles (Used for both Garland and Courier)
  bannerSection: {
    margin: 0,
    background: "linear-gradient(135deg, #ffffff 0%, #f2f8ff 100%)",
    borderRadius: "22px", padding: "30px 34px", minHeight:"118px",
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: "24px",
    boxShadow: "0 12px 32px rgba(34,65,100,.08)", border: "1px solid #e3edf7", flexWrap: "wrap",
    position: "relative", overflow: "hidden",
  },
  bannerText: { flex: 1, minWidth: "260px" },
  bannerTitle: { margin: "0 0 8px 0", fontSize: "24px", color: "#172033", fontWeight: "850", letterSpacing: "-.3px" },
  bannerSubtitle: { margin: 0, fontSize: "15px", color: "#666", lineHeight: 1.5 },
  bannerBtn: {
    padding: "9px 16px", background: "#172033", color: "#fff", border: "none",
    borderRadius: "10px", fontWeight: "800", fontSize: "12px", cursor: "pointer",
    whiteSpace: "nowrap", transition: "all .2s ease",
    boxShadow: "0 8px 18px rgba(23,32,51,.16)",
  },

  // Side-by-side Garland / 1-Day Courier image cards
  dualBannerRow: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-start",
    gap: "48px",
    padding: "0",
  },
  // Wrapper holds the heading (outside/above the image) + the image card
  imageBannerWrapper: {
    flex: "1 1 0",
    minWidth: "320px",
    display: "flex",
    flexDirection: "column",
  },
  // Matches sectionTitle exactly (same style/size as Vegetables & Flowers headings)
  imageBannerHeading: {
    fontSize: "25px",
    fontWeight: "850",
    margin: "0 0 20px 0",
    color: "#172033",
    letterSpacing: "-.4px",
  },
  imageBannerCard: {
    position: "relative",
    width: "100%",
    // aspect-ratio keeps the card proportions identical on every laptop
    // screen width instead of a fixed pixel height that would distort
    // as the card's width changes between screen sizes.
    aspectRatio: "2 / 1",
    borderRadius: "18px",
    overflow: "hidden",
    boxShadow: "0 10px 26px rgba(34,65,100,.14)",
    border: "1px solid #e3edf7",
  },
  imageBannerImg: {
    position: "absolute",
    top: 0, left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  imageBannerOverlay: {
    position: "absolute",
    top: 0, left: 0,
    width: "100%",
    height: "100%",
    background: "linear-gradient(180deg, rgba(23,32,51,0.42) 0%, rgba(23,32,51,0.05) 30%, rgba(23,32,51,0.1) 55%, rgba(23,32,51,0.88) 100%)",
  },
  imageBannerContent: {
    position: "relative",
    zIndex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    padding: "16px 18px 18px",
    boxSizing: "border-box",
  },
  imageBannerTextTop: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  imageBannerTitle: {
    margin: 0,
    fontSize: "15px",
    fontWeight: "850",
    color: "#fff",
    letterSpacing: "-.2px",
  },
  imageBannerSubtitle: {
    margin: 0,
    fontSize: "13px",
    color: "rgba(255,255,255,0.92)",
    lineHeight: 1.5,
    maxWidth: "92%",
    textShadow: "0 1px 3px rgba(0,0,0,0.35)",
  },
  imageBannerBtnRow: {
    display: "flex",
  },


  cartOverlay: {
    position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
    background: "rgba(0,0,0,0.6)", backdropFilter: "blur(2px)",
    zIndex: 9998,
  },
  cartWrapper: (showCart) => ({
    width: showCart ? "500px" : "0px",
    transition: "0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
    overflow: "hidden",
    backgroundColor: "#f4f6f9",
    height: "100vh",
    position: "fixed",
    right: 0,
    top: 0,
    boxShadow: showCart ? "-5px 0 25px rgba(0,0,0,0.15)" : "none",
    zIndex: 9999,
  }),
  overlay: { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.3)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 },
  loginBox: { position: "relative", background: "#fff", borderRadius: "16px", minWidth: "380px", boxShadow: "0 8px 24px rgba(0,0,0,0.15)" },
  closeBtn: { position: "absolute", right: "12px", top: "8px", border: "none", background: "transparent", fontSize: "28px", cursor: "pointer", zIndex: 10, color: "#666" },

  // ==========================================
  // FOOTER STYLES — 
  // ==========================================
  footer: {
    marginTop: "60px",
    backgroundColor: "#fff",
    borderTop: "1px solid #eee",
    padding: "56px 0 0",
  },
  footerTop: {
    maxWidth: "1400px",
    margin: "0 auto",
    padding: "0 130px",
    display: "flex",
    flexWrap: "wrap",
    gap: "48px",
    justifyContent: "space-between",
  },
  footerBrandCol: {
    flex: "1 1 320px",
    maxWidth: "360px",
  },
  footerLogoRow: {
    display: "flex",
    alignItems: "baseline",
    lineHeight: 0.9,
    transform: "skewX(-6deg)",
    marginBottom: "16px",
  },
  footerLogoShiper: {
    fontSize: "30px",
    fontWeight: "900",
    letterSpacing: "-1px",
    color:"#8ec5fc",
    textTransform: "uppercase",
  },
  footerLogoBox: {
    fontSize: "30px",
    fontWeight: "900",
    letterSpacing: "-1px",
    color: "#6e6e6e",
    textTransform: "uppercase",
    marginLeft: "3px",
  },
  footerTagline: {
    margin: "0 0 20px",
    fontSize: "14px",
    color: "#777",
    lineHeight: 1.6,
  },
  footerSocialRow: {
    display: "flex",
    gap: "10px",
  },
  footerSocialBtn: {
    width: "38px",
    height: "38px",
    borderRadius: "50%",
    background: "#f5f5f5",
    border: "1px solid #eee",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    textDecoration: "none",
    transition: "background 0.15s ease, transform 0.15s ease",
  },
  footerLinkCol: {
    flex: "0 1 200px",
    display: "flex",
    flexDirection: "column",
  },
  footerColTitle: {
    margin: "0 0 18px",
    fontSize: "14px",
    fontWeight: "700",
    color: "#111",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
  footerLink: {
    fontSize: "14px",
    color: "#666",
    textDecoration: "none",
    marginBottom: "14px",
    cursor: "pointer",
    width: "fit-content",
  },
  footerDivider: {
    height: "1px",
    background: "#eee",
    margin: "48px 0 0",
  },
  footerBottom: {
    maxWidth: "1400px",
    margin: "0 auto",
    padding: "20px 130px 28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "8px",
  },
  footerCopyright: {
    margin: 0,
    fontSize: "13px",
    color: "#999",
  },
  footerMadeWith: {
    margin: 0,
    fontSize: "13px",
    color: "#999",
  },
};